import { useMemo } from 'react'
import { UserService } from '@duck/api-definitions'
import { useTar } from '@/modules/i18n'
import { usePartnerDashboard } from './usePartnerDashboard'
import { usePersons } from './usePersons'
import { useTaskDisplayNames } from '@/modules/entityLookups'
import { getCustomStatus, type PartnerDashboardRow } from './TableSettings/partnerDashboardTable.utils'

// Dashboard data shaped for the cohorts + assignments tables. Shared by the
// partner dashboard and the admin organization detail page.
export const usePartnerDashboardRows = (organizationId?: string) => {
  const { t } = useTar()
  const { recentAssignments, cohortsWithStats, isLoading: isLoadingDashboard } =
    usePartnerDashboard(organizationId)

  const allPersonIds = useMemo(
    () => Array.from(new Set(recentAssignments.map((ad) => ad.personId))),
    [recentAssignments],
  )
  const { persons, isLoading: isLoadingPersons } = usePersons(allPersonIds)

  const taskIds = useMemo(
    () => Array.from(new Set(recentAssignments.map((ad) => ad.taskId))),
    [recentAssignments],
  )
  const { getDisplayName, isLoading: isTaskLoading } = useTaskDisplayNames(taskIds, {
    fallback: t('common.na'),
  })

  const flatRows = useMemo((): PartnerDashboardRow[] => {
    if (recentAssignments.length === 0) return []

    const personsMap = persons.reduce((acc, person) => {
      acc[person.id] = person
      return acc
    }, {} as Record<string, UserService.Person>)

    const rows: PartnerDashboardRow[] = []
    for (const ad of recentAssignments) {
      const person = personsMap[ad.personId]
      if (!person) continue

      rows.push({
        personId: ad.personId,
        assignmentId: ad.assignmentId,
        taskId: ad.taskId,
        taskDisplayName: getDisplayName(ad.taskId),
        email: person.email,
        onboardingStatus: person.onboardingStatus,
        assignmentStatus: ad.status,
        customStatus: getCustomStatus(person.onboardingStatus, ad.status),
        grade: ad.grade,
        feedback: ad.feedback,
        solution: ad.solution,
        startDate: ad.startDate,
        submitDate: ad.submitDate,
        workTime: ad.workTime,
        challengeId: ad.challengeId,
        charts: ad.charts,
        queries: ad.queries,
        chosenWeapon: ad.chosenWeapon,
        jNotebook: ad.jNotebook,
        assignmentSentDate: ad.createdAt,
        cohortName: ad.cohortName,
      })
    }
    return rows
  }, [recentAssignments, persons, getDisplayName])

  return {
    cohortsWithStats,
    flatRows,
    persons,
    isLoading: isLoadingDashboard || isLoadingPersons || isTaskLoading,
  }
}
